import { Component, ElementRef, EventEmitter, Input, Output, ViewChild, OnChanges, SimpleChanges, Injector, OnInit, AfterViewInit } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { debounceTime, distinctUntilChanged, Observable, Subscription, takeUntil } from 'rxjs';
import { BaseComponent } from 'src/app/common/base/base.component';
import { InsertOrigin, ResourceType, StatusResource } from 'src/app/common/enumerations/enumerations';
import { utils } from 'src/app/modules/libs/utils';
import { Resource, ResourceGroup } from 'src/app/components/catalogs/beamline-catalog/resource/resources';
import { getResourceDisplayName } from 'src/app/components/rsf/rsf.service';

@Component({
  selector: 'resource-chips',
  templateUrl: './resource-chips.component.html',
  styleUrls: ['./resource-chips.component.scss']
})
export class ResourceChipsComponent extends BaseComponent implements OnChanges, OnInit, AfterViewInit {
  @Input() placeholder!: string;
  @Input() disabled?: boolean;
  @Input() control: FormControl = new FormControl<Resource[]>([], { validators: Validators.required });
  @Input() selectedResources?: Resource[];
  @Input() hilite?: boolean | null;
  @Input() resourceTypes!: ResourceType[];
  @Input() noSubscript?: boolean;
  @Input() checkEmpty?: boolean;
  // Clears typed-only resource text when parent forms reuse this control for another record.
  @Input() resetKey?: string | number | null;
  @Output() onError = new EventEmitter<string>();
  @Output() onEdit = new EventEmitter<Resource[]>();

  @ViewChild('resourceAutocomplete') resourceAutocomplete?: MatAutocomplete;
  @ViewChild('resourceInput') resourceInput?: ElementRef<HTMLInputElement>;

  resources?: Resource[];
  resourceGroups!: ResourceGroupVm[];
  availableResources?: Resource[];
  filteredResources?: Resource[];

  insertOrigin = InsertOrigin;

  resources$!: Observable<Resource[]>;
  resourcesSubs!: Subscription;

  hasError?: boolean;

  filter?: string | null;

  inputCtrl = new FormControl<string>('');

  constructor(
    protected override injector: Injector
  ) {
    super(injector);
  }

  ngOnInit(): void {

  }

  /**
   * Keeps the chip control state aligned with parent inputs and clears transient autocomplete text on record switches.
   * @param changes Angular input changes for this component instance.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (this.disabled) this.control?.disable();
    else this.control?.enable();

    if (changes['resetKey']) {
      // Reset unsaved typed text so it does not carry into the next loaded form.
      this.clear();
    }

    // Preserve full relationship data while also supporting typed-text resets from reused parent forms.
    if (changes['selectedResources'] && this.resources?.length) {
      // Hydrate shared shielding relations before rebuilding the available-resource list.
      this.enrichSelectedResources();
      this.filterResources();
    }

    if (changes['resourceTypes']) this.loadResources();

    this.checkError();
  }

  ngAfterViewInit(): void {

  }

  loadResources() {
    this.resources$ = this.store.select(state => state.Resources.data);
    this.resourcesSubs = this.resources$.pipe(takeUntil(this.destroy$), debounceTime(300), distinctUntilChanged((prev, curr) => this.quickHash(prev) === this.quickHash(curr))).subscribe(data => {
      if (data?.length) {
        const clonedData = utils.cloneDeep(data);
        this.resources = this.processResources(clonedData)
          ?.sort((a, b) => utils.sortArrayAlphabeticallyWithComplexNumbers(a.name, b.name));
        this.filterResources();
        // Enrich selected resources with full data from store whenever store updates arrive
        if (this.selectedResources?.length) {
          this.enrichSelectedResources();
        }
        this.createResourceGroups();
      }
    });
  }

  /**
   * Builds the shared display label used for beamline resources and their shutter relations.
   * @param resource Resource whose display label is required.
   */
  private getBeamlineDisplayName(resource: Resource): string | undefined | null {
    // Use the shared function from rsf.service
    return getResourceDisplayName(resource);
  }

  /**
   * Enriches valid selected resources with the latest catalog relationships and display names.
   */
  enrichSelectedResources(): void {
    // Ignore incomplete navigation entries returned by form updates instead of dereferencing an undefined resource.
    this.selectedResources?.filter((resource): resource is Resource => !!resource).forEach(selectedResource => {
      const fullResource = this.resources?.find(r => r.id === selectedResource.id);
      if (fullResource) {
        // Always sync parentResources from the full resource
        selectedResource.parentResources = fullResource.parentResources;
        // Always update the display name with the full resource data to ensure shutter info is present
        const displayName = this.getBeamlineDisplayName(fullResource);
        if (selectedResource.name !== displayName) {
          selectedResource.name = displayName;
        }
      }
    });
  }

  processResources(data: Resource[]): Resource[] {
    return this.resourceTypes.flatMap(type => {
      const items = data.filter(resource => resource.type === type);

      if (type !== ResourceType.Beamline) {
        return items;
      }

      return items.map(resource => ({
        ...resource,
        name: this.getBeamlineDisplayName(resource)
      }));
    });
  }

  /**
   * Rebuilds the available resource list from the current selected chips and typed filter.
   */
  filterResources() {
    this.availableResources = this.resources?.filter(x => !this.selectedResources?.map(r => r?.id).includes(x.id));
    this.filteredResources = this.availableResources?.filter(x => x.name && x.name?.toLowerCase().includes(this.filter?.toLowerCase() ?? ''));
  }

  createResourceGroups() {
    const filter = (this.filter ?? '').toLowerCase();

    const nameMatches = (name?: string | null) =>
      !filter || (name ?? '').toLowerCase().includes(filter);

    this.resourceGroups = [];

    // Regular groups (Accelerator, Beamline, Shutter, Branchline...)
    this.resourceTypes.forEach(t => {
      if (t === ResourceType.AcceleratorLocation) return; // handled below

      if (!this.resourceGroups.some(g => g.type === t)) {
        let resources: Resource[] | undefined;

        switch (t) {
          case ResourceType.Accelerator:
            if (!this.resourceTypes.includes(ResourceType.AcceleratorLocation)) {
              resources = this.filteredResources?.filter(x => x.type === t && nameMatches(x.name));
            }
            break;

          case ResourceType.Beamline:
            resources = this.filteredResources?.filter(x => x.type === t && nameMatches(x.name));
            break;

          case ResourceType.Shutter:
            resources = this.filteredResources?.filter(
              x =>
                x.type === t &&
                nameMatches(x.name) &&
                !x.childResources?.some(c =>
                  c.childResource?.statusResourceId === StatusResource.Decommissioned ||
                  c.childResource?.statusResourceId === StatusResource.Inactive
                )
            );
            break;

          case ResourceType.Branchline:
            resources = this.filteredResources?.filter(
              x =>
                x.type === t &&
                nameMatches(x.name) &&
                !x.parentResources?.some(c =>
                  c.parentResource?.statusResourceId === StatusResource.Decommissioned ||
                  c.parentResource?.statusResourceId === StatusResource.Inactive
                )
            );
            break;
        }

        if (resources?.length) {
          const groupName = this.utils.humanizeEnumKey(
            this.utils.getEnumKeyByEnumValue(ResourceType, t) ?? ''
          ) ?? '';

          this.resourceGroups.push({
            name: groupName,
            type: t,
            items: resources.map(r => ({ resource: r, level: 0 }))
          });
        }
      }
    });

    // Accelerator Locations nested under each Accelerator (parent selectable)
    if (this.resourceTypes.includes(ResourceType.AcceleratorLocation)) {
      const accelerators = this.filteredResources?.filter(
        x => x.type === ResourceType.Accelerator && nameMatches(x.name)
      );

      accelerators?.forEach(acc => {
        acc.childResources?.forEach(cr => {
          if (!cr.childResource) cr.childResource = this.resources?.find(r => r.id === cr.childResourceID);
        });
        const locations =
          acc.childResources
            ?.filter(cr =>
              cr.childResourceType === ResourceType.AcceleratorLocation &&
              !this.selectedResources?.map(r => r.id).includes(cr.childResourceID ?? 0) &&
              nameMatches(cr.childResourceName)
            )
            .map(cr => cr.childResource!)
            .filter(Boolean)
            .sort((a, b) => utils.sortArrayAlphabeticallyWithComplexNumbers(a.name, b.name))
          ?? [];

        // Only show this accelerator group if it has matching children OR the accelerator itself matches
        const accMatches = nameMatches(acc.name);
        if (!accMatches && locations.length === 0) return;

        const items: ResourceItem[] = [
          { resource: acc, level: 0 },                 // selectable accelerator row
          ...locations.map(loc => ({ resource: loc, level: 1 as const })) // nested children
        ];

        this.resourceGroups.push({
          name: acc.name ?? '',
          type: ResourceType.AcceleratorLocation,
          items
        });
      });
    }
  }


  selected(event: MatAutocompleteSelectedEvent) {
    const resource: Resource = event.option.value;
    resource.insertOrigin = InsertOrigin.Manual;

    // Ensure the resource has the correct display name with beamline/shutter info
    // by looking it up from the fully-processed resources array before pushing.
    // This prevents the name from being lost when store updates arrive later.
    const fullResource = this.resources?.find(r => r.id === resource.id);
    if (fullResource) {
      resource.name = this.getBeamlineDisplayName(fullResource);
      // Also ensure parent/child relationships are preserved on the selected resource
      if (fullResource.parentResources) {
        resource.parentResources = fullResource.parentResources;
      }
    }

    this.selectedResources?.push(resource);
    this.clear();
    this.loadResources();
    this.createResourceGroups();
    this.inputCtrl.setValue('');
    this.onEdit.emit(this.selectedResources ?? []);
    this.checkError();
  }

  remove(resource: Resource) {
    const index = this.selectedResources?.indexOf(resource) ?? -1;
    if (index >= 0) {
      this.selectedResources?.splice(index, 1);
      this.clear();
      this.loadResources();
      this.createResourceGroups();
      this.onEdit.emit(this.selectedResources);
      this.checkError();
    }

  }

  /**
   * Clears the autocomplete text and filter while preserving selected chips.
   */
  clear() {
    if (this.resourceInput) this.resourceInput.nativeElement.value = '';
    this.inputCtrl.setValue('', { emitEvent: false });
    this.filter = null;
    this.filterResources();
    this.createResourceGroups();
  }

  /**
   * Clears unselected search text after the field loses focus while preserving autocomplete selection clicks.
   */
  clearInvalidText() {
    setTimeout(() => {
      const value = this.inputCtrl.value || this.resourceInput?.nativeElement.value;
      if (value)
        this.clear();
    }, 150);
  }

  checkError() {
    if (this.checkEmpty && !this.selectedResources?.length) {
      this.control.setErrors({ required: true });
      this.control.markAsTouched();
      this.hasError = true;
    }
    else {
      this.control.setErrors(null);
      this.hasError = false;
    }
  }

  /**
   * Updates autocomplete groups as the user types or clears unmatched text.
   * @param e Keyboard event from the resource search input.
   */
  onKeyUp(e: any) {
    const code = e.code;
    const text = e.target.value;
    if (!code?.includes("Arrow")) {
      this.filter = text;
      this.filterResources();
      this.createResourceGroups();
    }
  }


}

type ResourceItem = {
  resource: Resource;
  level: 0 | 1; // 0 = accelerator itself, 1 = child location
};

type ResourceGroupVm = {
  name: string;
  type: ResourceType;
  items: ResourceItem[];
};
